// Authentication context backed by the session cookie. `me` is fetched once and
// cached; login/bootstrap/logout mutate and refresh it.

import { createContext, useContext, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from './api.js';
import type { AuthUser } from './types.js';

interface Credentials {
  email: string;
  password: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  ownerMissing: boolean;
  login: (creds: Credentials) => Promise<void>;
  bootstrap: (creds: Credentials) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        const { data } = await api.get<AuthUser>('/auth/me');
        return data;
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 401) return null;
        throw err;
      }
    },
    retry: false,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['me'] });

  const loginMutation = useMutation({
    mutationFn: (creds: Credentials) => api.post<AuthUser>('/auth/login', creds),
    onSuccess: refresh,
  });
  const bootstrapMutation = useMutation({
    mutationFn: (creds: Credentials) => api.post<AuthUser>('/auth/bootstrap', creds),
    onSuccess: refresh,
  });
  const logoutMutation = useMutation({
    mutationFn: () => api.post('/auth/logout'),
    onSuccess: () => queryClient.clear(),
  });

  const value: AuthContextValue = {
    user: meQuery.data ?? null,
    isLoading: meQuery.isLoading,
    ownerMissing: false,
    login: async (creds) => {
      await loginMutation.mutateAsync(creds);
    },
    bootstrap: async (creds) => {
      await bootstrapMutation.mutateAsync(creds);
    },
    logout: async () => {
      await logoutMutation.mutateAsync();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
